import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Youtube, Play, Users, GraduationCap, TrendingUp, Award, Crown, ExternalLink, CheckCircle, Star } from 'lucide-react';

const YouTube = () => {
  const membershipTiers = [
    {
      level: 1,
      name: 'Investing in Canada',
      price: '',
      targetAudience: 'Beginners, Newcomers & DIY Investors',
      description: 'Perfect starting point for anyone new to Canadian investing.',
      perks: [
        'Access to Investing in Canada Masterclass',
        'TFSA investment strategies',
        'RRSP optimization techniques',
        'FHSA (First Home Savings Account) guide',
        'Portfolio building fundamentals',
        'Tax-efficient investing strategies',
      ],
      color: 'blue',
      icon: TrendingUp,
    },
    {
      level: 2,
      name: 'Industry Exam Prep',
      price: '',
      targetAudience: 'Aspiring Financial Advisors',
      description: 'Comprehensive preparation for Canadian securities licensing exams.',
      perks: [
        'All Level 1 perks included',
        'IFC (Investment Funds in Canada) video tutorials',
        'CSC (Canadian Securities Course) preparation',
        'CIRE (Canadian Investment Regulatory Exam) content',
        'Exam tips and study strategies',
        'Practice question walkthroughs',
      ],
      color: 'amber',
      icon: GraduationCap,
    },
    {
      level: 3,
      name: 'Advanced Industry Exams',
      price: '',
      targetAudience: 'Finance Professionals',
      description: 'Advanced exam preparation for experienced professionals.',
      perks: [
        'All Level 1 & 2 perks included',
        'RSE (Retail Securities Exam) preparation',
        'Additional CIRE preparation materials',
        'Advanced regulatory content',
        'Professional development resources',
        'Industry insights and updates',
      ],
      color: 'purple',
      icon: Award,
    },
    {
      level: 4,
      name: 'Ultimate Mentorship',
      price: '',
      targetAudience: 'Elite Professionals & Career Seekers',
      description: 'The complete package with personalized mentorship.',
      perks: [
        'All previous level perks included',
        'CFA designation support',
        'FRM (Financial Risk Management) resources',
        'Weekly Live Interaction Sessions',
        'Personalized career guidance',
        'Direct Q&A with Harry',
      ],
      color: 'gold',
      icon: Crown,
    },
  ];

  const channelStats = [
    { label: 'Subscribers', value: '11K+' },
    { label: 'Videos', value: '500+' },
    { label: 'Total Views', value: '1M+' },
  ];

  // Structured data for YouTube channel
  const youtubeSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Finance in Canada YouTube Channel - Membership",
    "description": "Join Finance in Canada YouTube channel membership for exclusive investing courses, IFC exam prep, CSC tutorials, CIRO preparation, and personalized mentorship.",
    "url": "https://harryvadalkar.ca/youtube",
    "mainEntity": {
      "@type": "Organization",
      "name": "Finance in Canada",
      "url": "https://www.youtube.com/@FinanceinCanada"
    }
  };

  return (
    <div className="min-h-screen" data-testid="youtube-page">
      <Helmet>
        <title>Finance in Canada YouTube Channel | Membership Tiers | IFC CSC CIRO Exam Prep | Investing Masterclass | Harry Vadalkar</title>
        <meta name="description" content="Join Finance in Canada YouTube channel membership with 11,000+ subscribers. 4 membership tiers: Investing in Canada Masterclass (TFSA, RRSP, FHSA), IFC/CSC/CIRO exam prep tutorials, RSE/CIRE preparation, and Ultimate Mentorship with CFA/FRM support and weekly live sessions with Harry Vadalkar." />
        <meta name="keywords" content="Finance in Canada YouTube, Finance in Canada channel, YouTube membership investing, investing course Canada YouTube, IFC exam prep YouTube, IFC tutorials YouTube, CSC course YouTube, CSC exam prep videos, CIRO exam YouTube, CIRO tutorials, RSE exam prep videos, CIRE exam YouTube, CFA prep YouTube, FRM prep videos, Harry Vadalkar YouTube, Canadian finance YouTube channel, investing tutorials Canada, TFSA guide YouTube, RRSP strategies YouTube, FHSA YouTube, Canadian investing masterclass, financial education YouTube, live investing sessions, Canadian finance education, wealth building YouTube, portfolio building tutorials" />
        <link rel="canonical" href="https://harryvadalkar.ca/youtube" />
        <script type="application/ld+json">
          {JSON.stringify(youtubeSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/30 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full mb-6">
              <Youtube className="w-5 h-5" />
              <span className="font-medium">YouTube Channel</span>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Finance in <span className="text-red-500">Canada</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Your ultimate destination for Canadian financial education. From beginner investing to professional exam preparation.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="https://www.youtube.com/@FinanceinCanada"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="youtube-channel-link"
                className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg"
              >
                <Youtube className="w-5 h-5 mr-2" />
                Visit Channel
              </a>
              <a
                href="https://www.youtube.com/channel/UCafePdh7G1fJt-meLdMrBdw/join"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="youtube-join-link"
                className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full transition-all shadow-lg"
              >
                <Star className="w-5 h-5 mr-2" />
                Join Membership
              </a>
            </div>

            {/* Channel Stats */}
            <div className="flex justify-center gap-8 md:gap-16 mb-12">
              {channelStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Featured Video */}
            <div className="max-w-3xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl shadow-red-500/20">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/UwKUFlINN-8"
                  title="Finance in Canada - Featured Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-20" data-testid="membership-tiers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Channel <span className="text-red-500">Membership</span> Tiers
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Choose the membership level that fits your learning goals. Each tier unlocks exclusive content and perks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {membershipTiers.map((tier, index) => (
              <div
                key={index}
                className={`glass rounded-2xl p-6 hover:border-${tier.color === 'gold' ? 'amber' : tier.color}-500/50 transition-all duration-300 flex flex-col relative ${
                  tier.level === 4 ? 'ring-2 ring-amber-500/50' : ''
                }`}
              >
                {tier.level === 4 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${
                    tier.color === 'blue' ? 'bg-blue-500/20' :
                    tier.color === 'amber' ? 'bg-amber-500/20' :
                    tier.color === 'purple' ? 'bg-purple-500/20' :
                    'bg-amber-500/20'
                  }`}>
                    <tier.icon className={`w-6 h-6 ${
                      tier.color === 'blue' ? 'text-blue-400' :
                      tier.color === 'amber' ? 'text-amber-400' :
                      tier.color === 'purple' ? 'text-purple-400' :
                      'text-amber-400'
                    }`} />
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    tier.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                    tier.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                    tier.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    Level {tier.level}
                  </span>
                </div>

                <h3 className="font-heading text-xl font-bold text-white mb-2">{tier.name}</h3>
                <p className={`text-sm mb-3 ${
                  tier.color === 'blue' ? 'text-blue-400' :
                  tier.color === 'amber' ? 'text-amber-400' :
                  tier.color === 'purple' ? 'text-purple-400' :
                  'text-amber-400'
                }`}>
                  {tier.targetAudience}
                </p>
                <p className="text-slate-400 text-sm mb-6">{tier.description}</p>

                <ul className="space-y-2 mb-6 flex-grow">
                  {tier.perks.map((perk, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        tier.color === 'blue' ? 'text-blue-400' :
                        tier.color === 'amber' ? 'text-amber-400' :
                        tier.color === 'purple' ? 'text-purple-400' :
                        'text-amber-400'
                      }`} />
                      <span className="text-slate-300 text-sm">{perk}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://www.youtube.com/channel/UCafePdh7G1fJt-meLdMrBdw/join"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`join-tier-${tier.level}`}
                  className={`inline-flex items-center justify-center font-semibold px-4 py-3 rounded-xl transition-all text-sm ${
                    tier.level === 4
                      ? 'bg-amber-500 hover:bg-amber-600 text-black'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Join Level {tier.level}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-slate-900/30" data-testid="what-you-get">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Why Join the <span className="text-red-500">Membership</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass rounded-xl p-8 text-center">
              <Play className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">Exclusive Content</h3>
              <p className="text-slate-400">
                Access members-only videos, tutorials, and masterclasses not available to regular subscribers.
              </p>
            </div>
            <div className="glass rounded-xl p-8 text-center">
              <Users className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">Community Access</h3>
              <p className="text-slate-400">
                Join a community of like-minded Canadian investors and finance professionals.
              </p>
            </div>
            <div className="glass rounded-xl p-8 text-center">
              <GraduationCap className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">Expert Guidance</h3>
              <p className="text-slate-400">
                Learn from a Harvard & MIT certified instructor with CFA, CSC, and MBA credentials.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="youtube-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to <span className="text-red-500">Level Up</span> Your Finance Knowledge?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join thousands of Canadians learning to invest smarter and build wealth.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.youtube.com/channel/UCafePdh7G1fJt-meLdMrBdw/join"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-join-cta"
              className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-4 rounded-full transition-all shadow-lg text-lg"
            >
              <Star className="w-5 h-5 mr-2" />
              Become a Member
            </a>
            <a
              href="https://www.youtube.com/@FinanceinCanada"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg text-lg"
            >
              <Youtube className="w-5 h-5 mr-2" />
              Subscribe Free
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouTube;
